import * as stylex from '@stylexjs/stylex'
import { Children, cloneElement, isValidElement, type ElementType, type ReactElement, type ReactNode } from 'react'
import { Box, type BoxProps } from '../Box/Box'

/**
 * https://github.com/mui/material-ui/blob/master/packages/mui-system/src/Stack/createStack.tsx
 * 抄 mui 的 divider props
 */

/**
 * Return an array with the separator React element interspersed between
 * each React node of the input children.
 *
 * > joinChildren([1,2,3], 0)
 * [1,0,2,0,3]
 */
function joinChildren(children: ReactNode, separator: ReactElement<unknown>) {
  const childrenArray = Children.toArray(children).filter(Boolean)

  return childrenArray.reduce<ReactNode[]>((output, child, index) => {
    output.push(child)

    if (index < childrenArray.length - 1) {
      output.push(cloneElement(separator, { key: `separator-${index}` }))
    }

    return output
  }, [])
}

const baseStyles = stylex.create({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
})

interface VStackExtendsProps {
  /**
   * 在每個子元素之間插入的分隔元素
   */
  divider?: ReactNode
}

export type VStackProps<RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement> = BoxProps<RootComponent, BoxElement> & VStackExtendsProps

/**
 * VStack 是垂直排列的 flex 容器（flexDirection: column）。
 * 繼承 Box 的所有 ExtendStyleProps，並額外支援 divider prop
 * 可在子元素之間自動插入分隔線。
 *
 * @example
 * ```tsx
 * <VStack gap={2}>
 *   <Text>項目 1</Text>
 *   <Text>項目 2</Text>
 * </VStack>
 * <VStack gap={2} divider={<Divider />}>
 *   <Text>項目 A</Text>
 *   <Text>項目 B</Text>
 * </VStack>
 * ```
 */
export function VStack<RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement>(
  props: VStackProps<RootComponent, BoxElement>,
) {
  const { component = 'div', rootStyle, divider, children: childrenProp, ...rest } = props
  const children = divider !== undefined && isValidElement(divider) ? joinChildren(childrenProp, divider) : childrenProp
  return (
    <Box component={component} rootStyle={[baseStyles.root, rootStyle]} {...rest}>
      {children}
    </Box>
  )
}
